import { isEqual } from 'lodash'
import { isBefore } from '@e3dc-react/shell/Libs/date'

import { RetrofitRecommendation, RetrofitConfiguration, InfinityOption, CabinetDimension } from './models'
import { getCurrentLanguage } from '../languages/functions/default'

type Recommendation = RetrofitRecommendation | InfinityOption

export const getBatteryModulesCount = (config: Recommendation): number => {
  const { numberModules } = config as InfinityOption

  // INFINITY UPGRADE CASE
  if (numberModules) {
    return numberModules
  }

  const { maxBatteryCount } = config as RetrofitRecommendation

  return maxBatteryCount
}

type ConfigOrRecommendation = Recommendation | RetrofitConfiguration

export const getPower = (config: ConfigOrRecommendation): string => {
  const language = getCurrentLanguage()

  return Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(config.power)
}

export const getCapacity = (config: ConfigOrRecommendation): string => {
  const language = getCurrentLanguage()

  return Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(config.capacity)
}

export const getBatteryModulesDiff = (currentSystem: RetrofitConfiguration, recommendation: Recommendation): string => {
  const numberModules = getBatteryModulesCount(recommendation)

  // INFINITY UPGRADE CASE
  if (numberModules) {
    const value = numberModules - currentSystem.currentBatteryCount

    return Intl.NumberFormat(getCurrentLanguage(), { maximumFractionDigits: 1 }).format(value)
  }

  const { addableBatteryCount } = recommendation as RetrofitRecommendation

  return Intl.NumberFormat(getCurrentLanguage(), { maximumFractionDigits: 1 }).format(addableBatteryCount)
}

export const getPowerDiff = (currentSystem: ConfigOrRecommendation, recommendation: Recommendation): string => {
  const value = recommendation.power - currentSystem.power

  return Intl.NumberFormat(getCurrentLanguage(), { maximumFractionDigits: 1 }).format(value)
}

export const getCapacityDiff = (currentSystem: ConfigOrRecommendation, recommendation: Recommendation): string => {
  const value = recommendation.capacity - currentSystem.capacity

  return Intl.NumberFormat(getCurrentLanguage(), { maximumFractionDigits: 1 }).format(value)
}

interface RecommendationForComparing {
  numberModules: number
  power: number
  capacity: number
  numberExternalCabinets: RetrofitRecommendation['numberExternalCabinets']
  info: RetrofitRecommendation['info']
}

interface RecommendationForComparingInfinityPlus {
  numberModules: number
  power: number
  capacity: number
}

export const compareTwoRecommendations = (firstRecommendation: RetrofitRecommendation, secondRecommendation?: RetrofitRecommendation): boolean => {
  const getSuggestionForComparing = (suggestion: RetrofitRecommendation): RecommendationForComparing => ({
    numberModules: getBatteryModulesCount(suggestion),
    power: suggestion.power,
    capacity: suggestion.capacity,
    numberExternalCabinets: suggestion.numberExternalCabinets,
    info: suggestion.info,
  })

  if (!secondRecommendation) {
    return false
  }

  const firstSuggestionForComparing = getSuggestionForComparing(firstRecommendation)
  const secondSuggestionForComparing = getSuggestionForComparing(secondRecommendation)

  return isEqual(firstSuggestionForComparing, secondSuggestionForComparing)
}

export const compareInfinityPlusRecommendations = (firstRecommendation: InfinityOption, secondRecommendation?: RetrofitRecommendation): boolean => {
  const getSuggestionForComparing = (suggestion: RetrofitRecommendation | InfinityOption): RecommendationForComparingInfinityPlus => ({
    numberModules: getBatteryModulesCount(suggestion),
    power: suggestion.power,
    capacity: suggestion.capacity,
  })

  if (!secondRecommendation) {
    return false
  }

  const firstSuggestionForComparing = getSuggestionForComparing(firstRecommendation)
  const secondSuggestionForComparing = getSuggestionForComparing(secondRecommendation)

  return isEqual(firstSuggestionForComparing, secondSuggestionForComparing)
}

export const checkIfRecommendationInvalid = (recommendation: Recommendation): boolean => {
  const batteries = getBatteryModulesCount(recommendation)
  const power = recommendation.power
  const capacity = recommendation.capacity

  return batteries <= 0 || power <= 0 || capacity <= 0
}

export const checkIfRecommendationOutdated = (recommendation: Recommendation): boolean => isBefore(new Date(recommendation.expiresAt), new Date())

// gets sizes in millimeters and convert it to centimeters
export const getCabinetSizes = (sizes: CabinetDimension | undefined): CabinetDimension | null => {
  const milimetersInCm = 10

  if (!sizes) {
    return null
  }

  const { depth, height, width } = sizes

  return {
    depth: +(depth / milimetersInCm).toFixed(),
    height: +(height / milimetersInCm).toFixed(),
    width: +(width / milimetersInCm).toFixed(),
  }
}
